import React from 'react';
import { useTranslation } from 'react-i18next';
import { Title, Text, Button, Card } from '@tremor/react';
import { SparklesIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import { useBehavioralArtifact } from './useBehavioralArtifact';
import UpgradedLoader from '../UpgradedLoader';

/**
 * Part 4: Behavioral Economics insights that cross the BE bases + documents + collected
 * results (focus group, etc.). Each insight has the three required fields.
 */
function BeInsightsTab({ projectUuid, analysisUuid }) {
    const { t } = useTranslation();
    const { status, result, loading, generate } = useBehavioralArtifact(projectUuid, analysisUuid, 'be_insights');

    const insights = Array.isArray(result) ? result : [];

    return (
        <div data-testid="be-insights-tab">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                    <LightBulbIcon className="h-6 w-6 text-iavisus-100" />
                    <Title className="text-lg font-semibold">{t('BehavioralEconomics.Insights.Title')}</Title>
                </div>
                {status !== 'processing' && (
                    <Button icon={SparklesIcon} onClick={generate} className="bg-iavisus-900">
                        {status === 'completed' ? t('BehavioralEconomics.Regenerate') : t('BehavioralEconomics.Generate')}
                    </Button>
                )}
            </div>
            <Text className="mb-4">{t('BehavioralEconomics.Insights.Description')}</Text>

            {(loading || status === 'processing') && <UpgradedLoader />}
            {status === 'failed' && <Text className="text-red-600">{t('BehavioralEconomics.Error')}</Text>}
            {status === 'not_started' && !loading && (
                <Text className="text-gray-500">{t('BehavioralEconomics.Insights.CollectDataFirst')}</Text>
            )}

            {status === 'completed' && insights.length > 0 && (
                <div className="space-y-4">
                    {insights.map((ins, i) => (
                        <Card key={i} className="p-4">
                            <Title className="text-base font-bold text-iavisus-100 mb-2">{ins.bias}</Title>
                            <div className="space-y-2">
                                <div>
                                    <Text className="font-bold text-xs uppercase text-gray-500">{t('BehavioralEconomics.Insights.Summary')}</Text>
                                    <Text>{ins.resumo_do_vies}</Text>
                                </div>
                                <div>
                                    <Text className="font-bold text-xs uppercase text-gray-500">{t('BehavioralEconomics.Insights.Relation')}</Text>
                                    <Text>{ins.relacao_com_estudo}</Text>
                                </div>
                                <div>
                                    <Text className="font-bold text-xs uppercase text-gray-500">{t('BehavioralEconomics.Insights.Suggestions')}</Text>
                                    <Text>{ins.sugestoes_de_implementacao}</Text>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

export default BeInsightsTab;
